import CloseIcon from '@mui/icons-material/Close';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { IconButton, ListItemIcon, ListItemText, MenuItem, Menu as MuiMenu } from '@mui/material';
import React, { MouseEvent, useState } from 'react';
import { slide as Menu } from 'react-burger-menu';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import EmailVerificationBanner from '../../components/EmailVerificationBanner/EmailVerificationBanner';
import { hasPasswordProvider } from '../../firebase/auth.utils';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectCurrentUser, selectIsAuthInitialized } from '../../redux/user/user.selectors';
import { signOutUser } from '../../redux/user/user.slice';
import './Navigation.scss';

const createKeyboardClickable = (onClick: () => void) => ({
  onClick,
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      onClick();
    }
  },
  role: 'button' as const,
  tabIndex: 0 as const,
});

function Navigation() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useMobileMediaQuery();

  const currentUser = useAppSelector(selectCurrentUser);
  const isAuthInitialized = useAppSelector(selectIsAuthInitialized);
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopMenuAnchorEl, setDesktopMenuAnchorEl] = useState<HTMLElement | null>(null);

  const signOut = () => {
    dispatch(signOutUser());
    navigate('/');
    setMenuOpen(false);
  };

  const handleMenuToggle = () => {
    setMenuOpen((open) => !open);
  };

  const handleDesktopMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setDesktopMenuAnchorEl(event.currentTarget);
  };

  const handleDesktopMenuClose = () => {
    setDesktopMenuAnchorEl(null);
  };

  const handleDesktopSettingsClick = () => {
    handleDesktopMenuClose();
    navigate('/account-settings');
  };

  const handleDesktopLogoutClick = () => {
    handleDesktopMenuClose();
    signOut();
  };

  const isLoggedIn = Boolean(currentUser);
  const showDesktopLinks = isLoggedIn && !isMobile;
  const showMobileMenu = isLoggedIn && isMobile;
  const desktopMenuOpen = Boolean(desktopMenuAnchorEl);

  const logoutButtonProps = createKeyboardClickable(signOut);
  const menuToggleProps = createKeyboardClickable(handleMenuToggle);

  if (!isAuthInitialized) {
    return null;
  }

  return (
    <>
      <div className='nav-container'>
        <Link
          className='nav-logo-container'
          to='/'
        >
          WANDER
        </Link>
        <div className='nav-links'>
          {!isLoggedIn && (
            <Link
              className='nav-link'
              to='/login'
            >
              LOG IN
            </Link>
          )}
          {showDesktopLinks && (
            <>
              <Link
                className='nav-link'
                to='/profile'
              >
                PROFILE
              </Link>
              <Link
                className={'nav-link'}
                to='/trips'
              >
                TRIPS
              </Link>
              <IconButton
                className='nav-menu-button'
                onClick={handleDesktopMenuOpen}
                aria-label='menu'
                disableRipple
              >
                <MenuIcon className='nav-menu-icon' />
              </IconButton>
              <MuiMenu
                className='nav-desktop-menu'
                anchorEl={desktopMenuAnchorEl}
                open={desktopMenuOpen}
                onClose={handleDesktopMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleDesktopSettingsClick}>
                  <ListItemIcon>
                    <SettingsOutlinedIcon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>SETTINGS</ListItemText>
                </MenuItem>
                <MenuItem
                  className='nav-desktop-menu-logout'
                  onClick={handleDesktopLogoutClick}
                >
                  <ListItemIcon>
                    <LogoutOutlinedIcon
                      fontSize='small'
                      className='nav-desktop-menu-logout-icon'
                    />
                  </ListItemIcon>
                  <ListItemText>LOG OUT</ListItemText>
                </MenuItem>
              </MuiMenu>
            </>
          )}
          {showMobileMenu && (
            <>
              <div
                className='menu-icon-container'
                {...menuToggleProps}
              >
                <MenuIcon className='nav-menu-icon' />
              </div>
              <Menu
                right
                width='65%'
                isOpen={menuOpen}
                onStateChange={(state) => setMenuOpen(state.isOpen)}
                customBurgerIcon={false}
                customCrossIcon={false}
                styles={{
                  bmMenu: {
                    background: '#ffffff',
                    padding: '1em',
                    zIndex: '1000',
                    transition: 'transform 0.3s ease-in-out',
                    boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.8)',
                    borderRadius: '10px 0 0 10px',
                    top: '0',
                    left: 'auto',
                    height: '100vh',
                    marginTop: '-40px',
                  },
                }}
              >
                <div
                  className='close-button-container'
                  {...menuToggleProps}
                >
                  <CloseIcon
                    className='close-icon'
                    fontSize='large'
                  />
                </div>
                <Link
                  className='nav-link trips'
                  to='/trips'
                  onClick={handleMenuToggle}
                >
                  TRIPS
                </Link>
                <Link
                  className='nav-link profile'
                  to='/profile'
                  onClick={handleMenuToggle}
                >
                  PROFILE
                </Link>
                <span
                  className='nav-link logout'
                  {...logoutButtonProps}
                >
                  LOG OUT
                </span>
              </Menu>
            </>
          )}
        </div>
      </div>
      {menuOpen && (
        <div
          className='overlay'
          {...menuToggleProps}
        />
      )}
      {/* Google accounts are verified by Google, so only email/password users see this. */}
      {currentUser && !currentUser.emailVerified && hasPasswordProvider(currentUser) && (
        <EmailVerificationBanner email={currentUser.email} />
      )}
      <Outlet />
    </>
  );
}

Navigation.displayName = 'Navigation';
export default Navigation;
